import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  ModalContainer,
  ContentModalContainer,
} from "../../../../../shared/components/modals";
import DesktopPriceUpdateView from "./components/DesktopPriceUpdateView";
import MobilePriceUpdateView from "./components/MobilePriceUpdateView";
import BatchPriceUpdate from "./components/BatchPriceUpdate";
import type {
  Product,
  Store,
  PriceUpdate,
  VariantPrice,
  PriceUpdateModalProps,
  PriceValidationError,
} from "../../types/ProductListing";
import PriceUpdateService from "../../services/priceUpdateService";
import ProductListingService from "../../services/productListingService";
import PriceValidationUtils from "../../utils/priceValidation";
import { formatVariantName } from "../../utils/variantUtils";

export interface PriceUpdateModalExtendedProps extends PriceUpdateModalProps {
  mode?: "modal" | "content";
  stores: Store[];
}

const PriceUpdateModal: React.FC<PriceUpdateModalExtendedProps> = ({
  isOpen,
  onClose,
  product,
  onSave,
  mode = "modal",
  stores,
}) => {
  const [variants, setVariants] = useState<VariantPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // Mobile viewport detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Service instance
  const priceUpdateService = useRef(PriceUpdateService.getInstance());

  const transformProductToVariantPrices = useCallback(
    (product: Product): VariantPrice[] => {
      if (
        !product.variantCombinations ||
        product.variantCombinations.length === 0
      ) {
        // Single variant product
        return [
          {
            variantId: "main",
            variantName: product.title,
            sku: product.sku,
            masterSku: product.merchantProduct?.sku || "N/A",
            marketplaceSku: product.sku,
            image: product.mainImage,
            currentPrice: product.price.current,
            newPrice: 0,
            currency: product.price.currency,
          },
        ];
      }

      return product.variantCombinations.filter(variant => (variant.isActive)).map((variant) => ({
        variantId: variant.variantId,
        variantName:
          formatVariantName(variant.tierVariation, true) || product.title,
        sku: variant.sku,
        masterSku:
          variant.merchantProduct?.sku || product.merchantProduct?.sku || "N/A",
        marketplaceSku: variant.sku,
        image: variant.photo || product.mainImage,
        currentPrice: variant.price.current,
        newPrice: 0,
        currency: variant.price.currency,
      }));
    },
    []
  );

  // Convert product data to variant price format
  useEffect(() => {
    if (isOpen) {
      const productToUse = product;
      if (productToUse) {
        const variantPrices = transformProductToVariantPrices(productToUse);
        setVariants(variantPrices);
        setError(null);
      }
    }
  }, [product, isOpen, transformProductToVariantPrices]);

  const handleBatchUpdate = (priceValue: number) => {
    const productToUse = product;
    if (!productToUse) return;

    // Validate the batch price for the marketplace
    const validation = PriceValidationUtils.validateMarketplacePrice(
      productToUse.marketplace,
      priceValue
    );

    if (!validation.valid) {
      setError(validation.error || "Invalid price");
      return;
    }

    setVariants((prev) =>
      prev.map((variant) => ({
        ...variant,
        newPrice: priceValue,
      }))
    );

    // Clear any previous errors
    setError(null);
    setValidationErrors({});
  };

  const handlePriceChange = (variantId: string, newPrice: number) => {
    const productToUse = product;
    if (!productToUse) return;

    // Update the variant price
    setVariants((prev) =>
      prev.map((variant) =>
        variant.variantId === variantId ? { ...variant, newPrice } : variant
      )
    );

    // Validate the new price for the marketplace
    if (newPrice > 0) {
      const validation = PriceValidationUtils.validateMarketplacePrice(
        productToUse.marketplace,
        newPrice
      );

      if (!validation.valid) {
        setValidationErrors((prev) => ({
          ...prev,
          [variantId]: validation.error || "Invalid price",
        }));
      } else {
        setValidationErrors((prev) => {
          const { [variantId]: removed, ...rest } = prev;
          return rest;
        });
      }
    } else {
      // Clear validation error for zero prices (optional fields)
      setValidationErrors((prev) => {
        const { [variantId]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSave = async () => {
    const productToUse = product;
    if (!productToUse) {
      setError("No product selected");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check for validation errors
      if (Object.keys(validationErrors).length > 0) {
        setError("Please fix price validation errors before saving");
        return;
      }

      // Filter variants with new prices
      const validVariants = variants.filter((variant) => variant.newPrice > 0);

      if (validVariants.length === 0) {
        setError("Please set at least one new price");
        return;
      }

      // Validate all prices for the marketplace
      const batchValidation =
        priceUpdateService.current.validateBatchPriceUpdates(
          validVariants,
          productToUse.marketplace
        );

      if (!batchValidation.valid) {
        const firstError = Object.values(batchValidation.errors)[0];
        setError(firstError || "Price validation failed");
        return;
      }

      // Call API to update prices
      console.log("[PriceUpdateModal] === API REQUEST ===");
      console.log("[PriceUpdateModal] Endpoint: product_listings/price");
      console.log("[PriceUpdateModal] Account ID:", productToUse.accountId);
      console.log("[PriceUpdateModal] Marketplace:", productToUse.marketplace);
      console.log("[PriceUpdateModal] Product SKU:", productToUse.sku);
      console.log("[PriceUpdateModal] Product Listing ID:", productToUse.productListingId);
      console.log("[PriceUpdateModal] Request payload:", JSON.stringify(validVariants, null, 2));

      const response = await priceUpdateService.current.updateProductPrices(
        productToUse.accountId,
        validVariants,
        productToUse.marketplace,
        productToUse.sku,
        productToUse.productListingId
      );

      // CRITICAL: Clear product listing cache so UI shows fresh data
      // Server updates database BEFORE responding, so cache must be invalidated immediately
      ProductListingService.getInstance().clearProductListingCache(
        productToUse.accountId
      );

      // Show success toast
      toast(
        `Price update submitted successfully! Updated ${validVariants.length} variant(s). Marketplace sync queued.`,
        {
          type: "success",
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Close modal immediately to show updated prices in the list
      onClose();

      // Refresh product list to show updated prices from database
      // No delay needed - server updates BEFORE responding (user confirmed)
      const priceUpdates: PriceUpdate[] = validVariants.map((variant) => ({
        variantId: variant.variantId,
        newPrice: variant.newPrice,
        currency: variant.currency,
      }));

      await onSave(priceUpdates);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update prices";
      setError(errorMessage);

      // Show error toast
      toast(errorMessage, {
        type: "error",
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    setValidationErrors({});
    onClose();
  };

  const productToUse = product;
  if (!isOpen || !productToUse) return null;

  // Render mobile view on small screens
  if (isMobile) {
    return (
      <MobilePriceUpdateView
        product={productToUse}
        variants={variants}
        onPriceChange={handlePriceChange}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={loading}
        error={error}
        stores={stores}
      />
    );
  }

  const productHeaderCard = (
    <>
      {/* Left side: Image + Title + Product info */}
      <div className="tw-flex tw-items-center tw-gap-4">
        <img
          alt="Product image"
          className="tw-h-14 tw-w-14 tw-rounded-lg tw-object-cover tw-shrink-0"
          src={
            productToUse.mainImage ||
            "https://via.placeholder.com/56x56?text=No+Image"
          }
        />
        <div className="tw-flex tw-flex-col">
          {/* Title beside image */}
          <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
            Update Product Pricing
          </h2>
          {/* Product info row */}
          <div className="tw-flex tw-items-center tw-gap-4 tw-text-sm tw-text-gray-500 tw-mt-1">
            <p>
              <span className="tw-font-medium tw-text-gray-700">Product:</span>{" "}
              {productToUse.title}
            </p>
            <div className="tw-h-4 tw-border-l tw-border-gray-300"></div>
            <p>
              <span className="tw-font-medium tw-text-gray-700">Store:</span>{" "}
              {stores.find((s) => s.id === productToUse.accountId)?.name ||
                productToUse.marketplace.charAt(0).toUpperCase() +
                  productToUse.marketplace.slice(1)}
            </p>
          </div>
        </div>
      </div>

      {/* Right side: Close button */}
      <button
        onClick={handleCancel}
        className="tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 tw-text-gray-600 tw-transition-colors"
        aria-label="Close modal"
        data-testid="close-button"
      >
        <span className="material-symbols-outlined tw-text-2xl">close</span>
      </button>
    </>
  );

  const footerContent = (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {error && (
        <div
          className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center"
          data-testid="price-modal-footer-error"
        >
          <p className="tw-text-red-800 tw-text-sm tw-flex-1">{error}</p>
        </div>
      )}
      <div className="tw-flex tw-justify-end tw-gap-3">
        <button
          onClick={handleCancel}
          className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-10 tw-px-4 tw-bg-white tw-text-gray-800 tw-text-sm tw-font-bold hover:tw-bg-gray-50 tw-border tw-border-gray-300"
          disabled={loading}
          data-testid="cancel-button"
        >
          <span className="tw-truncate">Cancel</span>
        </button>
        <button
          onClick={handleSave}
          disabled={loading || Object.keys(validationErrors).length > 0}
          className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-10 tw-px-4 tw-bg-primary tw-text-white tw-text-sm tw-font-bold hover:tw-bg-primary/90 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed"
          data-testid="update-button"
        >
          <span className="tw-truncate">
            {loading ? "Updating Prices..." : "Save Changes"}
          </span>
        </button>
      </div>
    </div>
  );

  const contentArea = (
    <div className="tw-flex-1 tw-overflow-y-auto tw-p-6 tw-space-y-6">
      <BatchPriceUpdate
        onApply={handleBatchUpdate}
        currency={variants[0]?.currency || "IDR"}
        marketplace={productToUse.marketplace}
      />

      <DesktopPriceUpdateView
        variants={variants}
        onPriceChange={handlePriceChange}
      />
    </div>
  );

  if (mode === "content") {
    return (
      <ContentModalContainer
        isOpen={isOpen}
        onClose={handleCancel}
        title="Update Product Pricing"
        customHeaderContent={productHeaderCard}
        footer={footerContent}
        testId="price-update-modal"
      >
        {contentArea}
      </ContentModalContainer>
    );
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleCancel}
      title="Update Product Pricing"
      customHeaderContent={productHeaderCard}
      hideDefaultTitle={true}
      size="xl"
      // className="!tw-max-w-7xl"
      fullscreenOverlay={true}
      footer={footerContent}
    >
      {contentArea}
    </ModalContainer>
  );
};

export default PriceUpdateModal;
